import React, { createContext, useContext, useEffect, useState } from "react";

interface GoogleMapsContextType {
  isLoaded: boolean;
  loadError: Error | null;
}

const GoogleMapsContext = createContext<GoogleMapsContextType | undefined>(
  undefined
);

export const useGoogleMaps = () => {
  const context = useContext(GoogleMapsContext);
  if (context === undefined) {
    throw new Error("useGoogleMaps must be used within a GoogleMapsApiView");
  }
  return context;
};

export const GoogleMapsApiView: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [state, setState] = useState<GoogleMapsContextType>({
    isLoaded: false,
    loadError: null,
  });

  useEffect(() => {
    // Check if Google Maps API is already loaded
    if (window.google && window.google.maps) {
      setState({ isLoaded: true, loadError: null });
      return;
    }

    // Load Google Maps API
    const scriptId = "google-maps-script";
    const existingScript = document.getElementById(scriptId);

    if (existingScript) {
      // Script already exists, wait for it to load
      const checkGoogleMapsLoaded = setInterval(() => {
        if (window.google && window.google.maps) {
          clearInterval(checkGoogleMapsLoaded);
          setState({ isLoaded: true, loadError: null });
        }
      }, 100);

      return () => clearInterval(checkGoogleMapsLoaded);
    }

    // Get API URL from environment variables
    const googleMapsApiUrl = import.meta.env.VITE_APP_GOOGLE_API_KEY;

    if (!googleMapsApiUrl) {
      setState({
        isLoaded: false,
        loadError: new Error("Google Maps API URL is not configured"),
      });
      return;
    }

    // Create and append script
    const script = document.createElement("script");
    script.id = scriptId;
    script.src = googleMapsApiUrl;
    script.async = true;
    script.defer = true;

    const handleScriptLoad = () => {
      setState({ isLoaded: true, loadError: null });
    };

    const handleScriptError = (error: ErrorEvent) => {
      setState({
        isLoaded: false,
        loadError: new Error(
          `Failed to load Google Maps API: ${error.message}`
        ),
      });
    };

    script.addEventListener("load", handleScriptLoad);
    script.addEventListener("error", handleScriptError);

    document.head.appendChild(script);

    return () => {
      if (script.parentNode) {
        script.removeEventListener("load", handleScriptLoad);
        script.removeEventListener("error", handleScriptError);
      }
    };
  }, []);

  return (
    <GoogleMapsContext.Provider value={state}>
      {children}
    </GoogleMapsContext.Provider>
  );
};
